import { useState } from 'react';
import { Button, Group, Modal, Select, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import type { DataObject } from '@/api/objects';
import { useIntakeTypes, useLaunchRun, type IntakeRunType, type RunType } from '@/api/runs';
import { ConfigForm, withDefaults, type ConfigValue, type JsonSchema } from './ConfigForm';

export interface LaunchRunModalProps {
  object: DataObject;
  type: IntakeRunType;
  opened: boolean;
  onClose: () => void;
  /** The same type launched again on a `ready` object; only the title differs (spec §4.2). */
  rerun?: boolean;
}

interface Version {
  name: string;
  description: string;
  config_schema: JsonSchema;
}

function versionsOf(rt: RunType): Version[] {
  return Array.isArray(rt.versions) ? (rt.versions as Version[]) : [];
}

/** Form state lives here and is remounted per version, so defaults come fresh from that schema. */
function LaunchForm({ object, type, rt, version, onClose }: { object: DataObject; type: IntakeRunType; rt: RunType; version: string; onClose: () => void }) {
  const { t } = useTranslation('runs');
  const { t: tc } = useTranslation();
  const launch = useLaunchRun(object.public_id);
  const schema = versionsOf(rt).find((v) => v.name === version)?.config_schema ?? (rt.config_schema as JsonSchema) ?? { type: 'object' };
  const [config, setConfig] = useState<ConfigValue>(() => withDefaults(schema, {}));
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      await launch.mutateAsync({ type, version, config: config as Record<string, never> });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Stack gap="md">
      <ConfigForm schema={schema} value={config} onChange={setConfig} />
      {error && (
        <Text c="red" role="alert" fz={14}>
          {error}
        </Text>
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          {tc('common.cancel')}
        </Button>
        <Button loading={launch.isPending} onClick={() => void submit()}>
          {t('launch.start')}
        </Button>
      </Group>
    </Stack>
  );
}

export function LaunchRunModal({ object, type, opened, onClose, rerun = false }: LaunchRunModalProps) {
  const { t } = useTranslation('runs');
  const { data: types } = useIntakeTypes(object.public_id);
  const rt = types?.[type] ?? undefined;
  const [version, setVersion] = useState<string | null>(null);
  const chosen = version ?? rt?.default_version ?? versionsOf(rt ?? ({} as RunType))[0]?.name ?? null;

  return (
    <Modal opened={opened} onClose={onClose} title={rerun ? t('launch.rerun') : t(`launch.${type}`)}>
      <Stack gap="md">
        {rt && versionsOf(rt).length > 1 && (
          <Select
            label={t('launch.version')}
            data={versionsOf(rt).map((v) => ({ value: v.name, label: `${v.name} · ${v.description}` }))}
            value={chosen}
            onChange={setVersion}
            allowDeselect={false}
          />
        )}
        {rt && chosen ? (
          <LaunchForm key={chosen} object={object} type={type} rt={rt} version={chosen} onClose={onClose} />
        ) : (
          <Text fz={14} c="red">
            {t('launch.unavailable')}
          </Text>
        )}
      </Stack>
    </Modal>
  );
}
